import type { JobResponse, FirmResponse } from "@taxassist/api-client";
import { JobStatus } from "@taxassist/api-client";

/**
 * Determines if a job can be submitted for processing
 * Note: This only checks job-level requirements, not firm credit availability
 */
export const canSubmitJob = (job: JobResponse): boolean => {
  // Can't submit if already in submitting state to prevent race conditions
  if (job.status === JobStatus.SUBMITTED) {
    return false;
  }

  // Must have at least 1 uploaded file
  if (!job.uploadedFiles || job.uploadedFiles.length === 0) {
    return false;
  }

  // For DRAFT jobs, allow submission if any files exist (all are considered "new")
  if (job.status === JobStatus.DRAFT) {
    return job.uploadedFiles.length > 0;
  }

  // For jobs that have been submitted before, check if there are new files uploaded after last submission
  if (job.lastSubmittedAt) {
    // Check if any files were uploaded after the last submission
    const hasNewFiles = job.uploadedFiles.some((file) => {
      // If file doesn't have uploadedAt timestamp, consider it as old file
      if (!file.uploadedAt) return false;

      // Compare file upload time with last submission time
      return new Date(file.uploadedAt) > new Date(job.lastSubmittedAt!);
    });

    return hasNewFiles;
  }

  // For other statuses without lastSubmittedAt, allow submission if files exist
  return job.uploadedFiles.length > 0;
};

/**
 * Determines if a job can be submitted considering both job state and firm credits
 */
export const canSubmitJobWithCredits = (
  job: JobResponse,
  firmData?: FirmResponse,
): boolean => {
  // First check basic job submission requirements
  if (!canSubmitJob(job)) {
    return false;
  }

  // Check credit availability
  if (firmData && firmData.totalCredits <= 0) {
    // For DRAFT jobs (first submission), require credits
    if (job.status === JobStatus.DRAFT) {
      return false;
    }

    // For NEW_FILES status or any job with previous submissions,
    // allow submission even with 0 credits since credits were already deducted during file upload
    if (job.status === JobStatus.NEW_FILES || job.lastSubmittedAt) {
      return true;
    }

    // For other cases with no credits, block submission
    return false;
  }

  return true;
};

/**
 * Gets the appropriate tooltip text for the submit button
 */
export const getSubmitTooltip = (
  job: JobResponse,
  isSubmitting: boolean,
  firmData?: FirmResponse,
): string => {
  if (isSubmitting) {
    return "Submitting...";
  }

  // Check credit availability for DRAFT jobs (first submission)
  if (firmData && firmData.totalCredits <= 0) {
    if (job.status === JobStatus.DRAFT) {
      return "No credits available for job submission";
    }
  }

  if (!canSubmitJob(job)) {
    if (job.status === JobStatus.SUBMITTED) {
      return "Job is currently being submitted";
    }
    if (!job.uploadedFiles || job.uploadedFiles.length === 0) {
      return "Upload files before submitting";
    }
    if (job.lastSubmittedAt) {
      return "No new files to submit";
    }
    return "Cannot submit job";
  }

  const newFilesCount = getNewFilesCount(job);
  const submittedFilesCount = getSubmittedFilesCount(job);

  if (job.status === JobStatus.DRAFT) {
    return newFilesCount === 1
      ? "Submit job with 1 file for processing"
      : `Submit job with ${newFilesCount} files for processing`;
  }

  // For jobs that have been submitted before
  if (job.lastSubmittedAt && submittedFilesCount > 0) {
    return newFilesCount === 1
      ? "Submit 1 new file for processing"
      : `Submit ${newFilesCount} new files for processing`;
  }

  // Fallback for other cases
  return newFilesCount === 1
    ? "Submit job with 1 file for processing"
    : `Submit job with ${newFilesCount} files for processing`;
};

/**
 * Gets the submitted files from a job based on lastSubmittedAt timestamp
 */
export const getSubmittedFiles = (job: JobResponse) => {
  if (!job.uploadedFiles || !job.lastSubmittedAt) {
    return [];
  }

  // Files uploaded at or before lastSubmittedAt are considered submitted
  return job.uploadedFiles.filter((file) => {
    if (!file.uploadedAt) return false;
    return new Date(file.uploadedAt) <= new Date(job.lastSubmittedAt!);
  });
};

/**
 * Gets the count of submitted files
 */
export const getSubmittedFilesCount = (job: JobResponse): number => {
  return getSubmittedFiles(job).length;
};

/**
 * Gets new (unsubmitted) files from a job based on lastSubmittedAt timestamp
 */
export const getNewFiles = (job: JobResponse) => {
  if (!job.uploadedFiles) {
    return [];
  }

  // For DRAFT jobs or jobs without lastSubmittedAt, all files are considered new
  if (job.status === JobStatus.DRAFT || !job.lastSubmittedAt) {
    return job.uploadedFiles;
  }

  // Files uploaded after lastSubmittedAt are considered new
  return job.uploadedFiles.filter((file) => {
    if (!file.uploadedAt) return false;
    return new Date(file.uploadedAt) > new Date(job.lastSubmittedAt!);
  });
};

/**
 * Gets the count of new (unsubmitted) files
 */
export const getNewFilesCount = (job: JobResponse): number => {
  return getNewFiles(job).length;
};
/**
 * Helper function to determine if a job is currently active
 * Uses the active field from the backend, which follows isJobStatusActive() logic
 */
export const isJobActive = (job: JobResponse): boolean => {
  // Use the active field from the API response, defaulting to false if not provided
  return job.active ?? false;
};
/**
 * Helper function to determine if the Start Processing button should be enabled
 * Requirements:
 * 1. Job is assigned (has assignedReviewer)
 * 2. Job is active (not draft/cancelled/archived)
 * 3. Job is in TRIAGE state OR has files with submittedToSurePrep = false
 */
export const canStartProcessing = (job: JobResponse): boolean => {
  // Check if job is assigned
  if (!job.assignedReviewer) {
    return false;
  }

  // Check if job is active
  if (!isJobActive(job)) {
    return false;
  }

  // For other active states, check if there are files not submitted to SurePrep
  // Since the API doesn't support submittedToSurePrep yet, use the utility function
  // which currently returns all files as "new" (not submitted)
  const newFilesCount = getNewFilesCount(job);
  return newFilesCount > 0;
};
